import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- 401 Interceptor: handle session expiration ---
// --- 403 workspaceUnavailable: the person's workspace has been paused ---
let isRedirecting = false;

// Signed-out pages: the sign-in page and the pages an emailed link opens.
// A paused workspace must not bounce someone off these.
const PUBLIC_AUTH_PAGES = ['/login', '/signup', '/forgot-password', '/reset-password', '/accept-invite', '/verify-email'];

// Calls that work signed out and show their own errors in place. A 401 or
// a paused-workspace 403 from one of these must reach the page, not redirect.
const PUBLIC_AUTH_CALLS = [
  '/auth/login',
  '/auth/password/forgot',
  '/auth/password/reset',
  '/auth/token/check',
  '/auth/invite/accept',
  '/auth/verify-email',
  '/signup',
];

export const isPublicAuthCall = (url) =>
  PUBLIC_AUTH_CALLS.includes(String(url || '').split('?')[0].replace(/\/+$/, ''));

export const onPublicAuthPage = () => {
  const base = process.env.PUBLIC_URL || '';
  const path = window.location.pathname || '';
  return PUBLIC_AUTH_PAGES.includes((path.startsWith(base) ? path.slice(base.length) : path).replace(/\/+$/, ''));
};

// --- 402 billingRequired: the workspace's trial ended or its subscription
// lapsed. Nothing is deleted. Every call but auth and billing answers 402
// until an Admin subscribes, so there is no redirect here (a redirect would
// only meet another 402 and loop). Instead the app is told, and it shows the
// locked screen in place of its pages.
const billingRequiredListeners = new Set();

export const onBillingRequired = (listener) => {
  billingRequiredListeners.add(listener);
  return () => billingRequiredListeners.delete(listener);
};

export const isBillingRequired = (error) =>
  !!(error && error.response && error.response.status === 402 && error.response.data?.billingRequired);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isBillingRequired(error)) {
      const requestUrl = error.config?.url || '';
      if (!isPublicAuthCall(requestUrl) && !onPublicAuthPage()) {
        billingRequiredListeners.forEach((listener) => {
          try { listener(error.response.data); } catch (e) { /* a listener must not break the call */ }
        });
      }
      return Promise.reject(error);
    }

    // A paused workspace blocks every call. Send the person to the login
    // page once, with a note saying why. The login call itself shows the
    // server's message in place, and the login page's own /auth/me check
    // must not redirect again or it would loop.
    if (
      error.response &&
      error.response.status === 403 &&
      error.response.data?.workspaceUnavailable &&
      !isRedirecting
    ) {
      const requestUrl = error.config?.url || '';
      if (!requestUrl.includes('/auth/login') && !isPublicAuthCall(requestUrl) && !onPublicAuthPage()) {
        isRedirecting = true;
        window.location.href = (process.env.PUBLIC_URL || '') + '/login?workspace=paused';
      }
      return Promise.reject(error);
    }

    if (
      error.response &&
      error.response.status === 401 &&
      !isRedirecting
    ) {
      const requestUrl = error.config?.url || '';
      const isAuthCheck =
        requestUrl.includes('/auth/me') ||
        requestUrl.includes('/auth/check') ||
        requestUrl.includes('/auth/login') ||
        isPublicAuthCall(requestUrl);

      if (!isAuthCheck) {
        isRedirecting = true;
        window.location.href = (process.env.PUBLIC_URL || '') + '/login?session=expired';
      }
    }
    return Promise.reject(error);
  }
);

// Wrap reference-data fetches that can 403 when the admin has hidden that
// module for the current user. A hidden module must degrade to an empty
// list — never break the page that asked for the data alongside it.
export const optional = (promise, fallback = []) =>
  promise.catch(() => ({ data: fallback }));

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const logout = () => api.post('/auth/logout');
export const getCurrentUser = () => api.get('/auth/me');
export const changePassword = (currentPassword, newPassword) =>
  api.post('/auth/change-password', { currentPassword, newPassword });
export const getLoginHistory = (params = {}) => api.get('/auth/login-history', { params });

// Emailed links: invites, password resets and email verification. All but
// sendVerificationEmail work signed out. purpose is 'invite', 'reset' or 'verify'.
export const forgotPassword = (email) => api.post('/auth/password/forgot', { email });
export const checkToken = (token, purpose) => api.post('/auth/token/check', { token, purpose });
export const resetPassword = (token, password) => api.post('/auth/password/reset', { token, password });
export const acceptInvite = (token, password) => api.post('/auth/invite/accept', { token, password });
export const verifyEmail = (token) => api.post('/auth/verify-email', { token });
export const sendVerificationEmail = () => api.post('/auth/verify-email/send');

// Access requests: the landing page posts signed out; admins read and work the list.
export const requestAccess = (data) => api.post('/access-requests', data);
export const getAccessRequests = (params = {}) => api.get('/access-requests', { params });
export const updateAccessRequest = (id, status) => api.patch(`/access-requests/${id}`, { status });

// Platform customers: only the platform owner (signed in to the original
// workspace) can call these. Each customer is a company with its own database.
export const getCustomers = () => api.get('/platform/customers');
// data.send_invite: true emails the first admin an invite instead of making a temporary password.
export const createCustomer = (data) => api.post('/platform/customers', data);
export const suspendCustomer = (id) => api.post(`/platform/customers/${id}/suspend`);
export const reactivateCustomer = (id) => api.post(`/platform/customers/${id}/reactivate`);
// Resends the first admin's set-password invite while it is still pending.
export const resendCustomerInvite = (id) => api.post(`/platform/customers/${id}/resend-invite`);
// The customer's monthly AI allowance in tokens; null for no cap.
export const setCustomerAiLimit = (id, monthlyTokens) => api.put(`/platform/customers/${id}/ai-limit`, { monthly_tokens: monthlyTokens });

// Whether this customer's workspace is billed, and free of charge. The
// primary workspace is never billed. Returns { customer }.
export const setCustomerBillingFree = (id, free) => api.put(`/platform/customers/${id}/billing`, { free: !!free });

// Billing for the signed-in workspace: per user per month through Stripe.
// Card details never reach our server. Checkout and the customer portal
// are Stripe pages; both calls return { url } to send the browser to.
export const getBilling = () => api.get('/billing');
export const startBillingCheckout = () => api.post('/billing/checkout');
export const openBillingPortal = () => api.post('/billing/portal');

// Self sign-up: a company asks for its own workspace (signed out); the
// platform owner approves or declines it in Settings > Customers.
export const signUp = (data) => api.post('/signup', data);
export const getSignupRequests = (status = 'pending') => api.get('/platform/signup-requests', { params: { status } });
export const approveSignupRequest = (id, data = {}) => api.post(`/platform/signup-requests/${id}/approve`, data);
export const declineSignupRequest = (id) => api.post(`/platform/signup-requests/${id}/decline`);

// Users
export const getUsers = () => api.get('/users');
export const getUser = (id) => api.get(`/users/${id}`);
// data.send_invite: true emails an invite and needs no password.
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const resetUserPassword = (id, newPassword) => api.post(`/users/${id}/reset-password`, { newPassword });
export const deactivateUser = (id, reassignTo) => api.post(`/users/${id}/deactivate`, { reassignTo });
export const reactivateUser = (id) => api.post(`/users/${id}/reactivate`);
// Sends an invite, or a set-password link to someone who already has a password.
export const inviteUser = (id) => api.post(`/users/${id}/invite`);

// Impersonation
export const impersonateUser = (userId) => api.post(`/auth/impersonate/${userId}`);
export const stopImpersonation = () => api.post('/auth/stop-impersonation');

// Roles
export const getRoles = () => api.get('/roles');
export const getRole = (id) => api.get(`/roles/${id}`);
export const createRole = (data) => api.post('/roles', data);
export const updateRole = (id, data) => api.put(`/roles/${id}`, data);
export const deleteRole = (id) => api.delete(`/roles/${id}`);

// Accounts
export const getAccounts = (params) => api.get('/accounts', { params });
export const getAccount = (id) => api.get(`/accounts/${id}`);
export const checkDuplicateAccount = (name) => api.get(`/accounts/check-duplicate/${encodeURIComponent(name)}`);
export const createAccount = (data) => api.post('/accounts', data);
export const updateAccount = (id, data) => api.put(`/accounts/${id}`, data);
export const deleteAccount = (id, deleteContacts) => api.delete(`/accounts/${id}`, { params: { deleteContacts } });
export const bulkUpdateAccounts = (ids, updates) => api.post('/accounts/bulk-update', { ids, updates });
export const bulkDeleteAccounts = (ids, deleteContacts) => api.post('/accounts/bulk-delete', { ids, deleteContacts });
export const bulkSetPrimaryOwner = (ids, user_id) => api.post('/accounts/bulk-set-primary-owner', { ids, user_id });
export const bulkAddOwner = (ids, user_id) => api.post('/accounts/bulk-add-owner', { ids, user_id });
export const importAccounts = (records, updateExisting) => api.post('/accounts/import', { records, updateExisting });

// Account Addresses
export const getAccountAddresses = (accountId) => api.get(`/accounts/${accountId}/addresses`);
export const createAccountAddress = (accountId, data) => api.post(`/accounts/${accountId}/addresses`, data);
export const updateAccountAddress = (accountId, addressId, data) => api.put(`/accounts/${accountId}/addresses/${addressId}`, data);
export const deleteAccountAddress = (accountId, addressId) => api.delete(`/accounts/${accountId}/addresses/${addressId}`);

// Account Field Updates (single-field patch with audit trail)
export const updateAccountField = (accountId, data) => api.patch(`/accounts/${accountId}/field`, data);
export const getAccountFieldChanges = (accountId, params) => api.get(`/accounts/${accountId}/field-changes`, { params });

// Account Hierarchy / Account Map — accounts form a strict single-parent tree.
// Every write returns the full family-tree state; the client replaces local
// state with the response (mirrors the influence-map endpoints).
export const getAccountHierarchy = (accountId) => api.get(`/account-hierarchy/account/${accountId}`);
export const setAccountParent = (accountId, parentAccountId) => api.patch(`/account-hierarchy/account/${accountId}/parent`, { parent_account_id: parentAccountId });
export const updateAccountHierarchyPosition = (accountId, x, y) => api.patch(`/account-hierarchy/account/${accountId}/position`, { x, y });
export const addAccountPlaceholder = (accountId, data) => api.post(`/account-hierarchy/account/${accountId}/placeholders`, data);
export const updateAccountPlaceholder = (placeholderId, data) => api.patch(`/account-hierarchy/placeholders/${placeholderId}`, data);
export const deleteAccountPlaceholder = (placeholderId) => api.delete(`/account-hierarchy/placeholders/${placeholderId}`);
export const promoteAccountPlaceholder = (placeholderId, data) => api.post(`/account-hierarchy/placeholders/${placeholderId}/promote`, data);

// Account Org Chart — reporting hierarchy among an account's contacts. Every
// write returns the full org state (same convention as the maps above).
export const getAccountOrg = (accountId) => api.get(`/account-org/account/${accountId}`);
export const setContactManager = (contactId, managerContactId) => api.patch(`/account-org/contact/${contactId}/manager`, { manager_contact_id: managerContactId });
export const setContactOrgPosition = (contactId, x, y) => api.patch(`/account-org/contact/${contactId}/position`, { x, y });
export const setContactPlacement = (contactId, onChart) => api.patch(`/account-org/contact/${contactId}/placement`, { on_chart: onChart });
export const setContactOrgPositions = (positions) => api.patch('/account-org/positions', { positions });

// Opportunities
export const getOpportunities = (params) => api.get('/opportunities', { params });
export const getOpportunity = (id) => api.get(`/opportunities/${id}`);
export const createOpportunity = (data) => api.post('/opportunities', data);
export const updateOpportunity = (id, data) => api.put(`/opportunities/${id}`, data);
export const deleteOpportunity = (id) => api.delete(`/opportunities/${id}`);
export const bulkUpdateOpportunities = (ids, updates) => api.post('/opportunities/bulk-update', { ids, updates });
export const bulkDeleteOpportunities = (ids) => api.post('/opportunities/bulk-delete', { ids });
export const getOpportunityStages = () => api.get('/opportunities/stages/list');

// Contacts
export const getContacts = (params) => api.get('/contacts', { params });
export const getContact = (id) => api.get(`/contacts/${id}`);
export const createContact = (data) => api.post('/contacts', data);
export const updateContact = (id, data) => api.put(`/contacts/${id}`, data);
export const deleteContact = (id) => api.delete(`/contacts/${id}`);
export const bulkUpdateContacts = (ids, updates) => api.post('/contacts/bulk-update', { ids, updates });
export const importContacts = (records, updateExisting) => api.post('/contacts/import', { records, updateExisting });

// Influence Maps — one map per account, server-side persisted.
// All write endpoints return the full updated map so the client just
// replaces local state with the response.
export const getInfluenceMap = (accountId) => api.get(`/influence-maps/by-account/${accountId}`);
export const getInfluenceMapSuggestions = () => api.get('/influence-maps/suggestions');
export const hideInfluenceMapSuggestion = (type, name) => api.post('/influence-maps/suggestions/hide', { type, name });
export const unhideInfluenceMapSuggestion = (type, name) => api.post('/influence-maps/suggestions/unhide', { type, name });
export const updateInfluenceMap = (mapId, data) => api.put(`/influence-maps/${mapId}`, data);
export const renameMapGroup = (mapId, from, to) => api.post(`/influence-maps/${mapId}/groups/rename`, { from, to });
export const deleteMapRow = (mapId, index) => api.delete(`/influence-maps/${mapId}/rows/${index}`);
export const reorderMapRows = (mapId, from, to) => api.post(`/influence-maps/${mapId}/rows/reorder`, { from, to });
export const setMapRowPosition = (mapId, index, y) => api.patch(`/influence-maps/${mapId}/rows/${index}/position`, { y });

// Admin — destructive system actions, gated server-side on
// manage_settings permission.
export const getFactoryResetPreview = () => api.get('/admin/factory-reset/preview');
export const factoryReset = (confirmPhrase) => api.post('/admin/factory-reset', { confirmPhrase });
export const addMapPerson = (mapId, data) => api.post(`/influence-maps/${mapId}/people`, data);
export const updateMapPerson = (mapId, personId, data) => api.patch(`/influence-maps/${mapId}/people/${personId}`, data);
export const removeMapPerson = (mapId, personId) => api.delete(`/influence-maps/${mapId}/people/${personId}`);
export const promoteMapPerson = (mapId, personId, data) => api.post(`/influence-maps/${mapId}/people/${personId}/promote`, data);
export const addMapLink = (mapId, data) => api.post(`/influence-maps/${mapId}/links`, data);
export const updateMapLink = (mapId, linkId, data) => api.patch(`/influence-maps/${mapId}/links/${linkId}`, data);
export const removeMapLink = (mapId, linkId) => api.delete(`/influence-maps/${mapId}/links/${linkId}`);

// Rep Firms
export const getRepFirms = (params) => api.get('/rep-firms', { params });
export const getRepFirm = (id) => api.get(`/rep-firms/${id}`);
export const createRepFirm = (data) => api.post('/rep-firms', data);
export const updateRepFirm = (id, data) => api.put(`/rep-firms/${id}`, data);
export const deleteRepFirm = (id) => api.delete(`/rep-firms/${id}`);

// Products
export const getProducts = (params) => api.get('/products', { params });
export const getProduct = (id) => api.get(`/products/${id}`);
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);
export const bulkUpdateProducts = (products) => api.post('/products/bulk-update', { products });
export const exportProductsCsv = () => api.get('/products/export/csv', { responseType: 'blob' });
export const importProductsCsv = (csvData, updateExisting) => 
  api.post('/products/import/csv', { csvData, updateExisting });

// Quotes
export const getQuotes = (params) => api.get('/quotes', { params });
export const getQuote = (id) => api.get(`/quotes/${id}`);
export const createQuote = (data) => api.post('/quotes', data);
export const updateQuote = (id, data) => api.put(`/quotes/${id}`, data);
export const updateQuoteStatus = (id, status) => api.patch(`/quotes/${id}/status`, { status });
export const deleteQuote = (id) => api.delete(`/quotes/${id}`);
export const generateQuotePdf = (id, templateId) =>
  api.post(`/quotes/${id}/generate-pdf${templateId ? `?template_id=${templateId}` : ''}`);
export const convertQuoteToOrder = (id) => api.post(`/quotes/${id}/convert-to-order`);
export const bulkUpdateQuotes = (ids, updates) => api.post('/quotes/bulk-update', { ids, updates });

// Orders
export const getOrders = (params) => api.get('/orders', { params });
export const getOrder = (id) => api.get(`/orders/${id}`);
export const createOrder = (data) => api.post('/orders', data);
export const updateOrder = (id, data) => api.put(`/orders/${id}`, data);
export const deleteOrder = (id) => api.delete(`/orders/${id}`);
export const updateOrderStatus = (id, status) => api.patch(`/orders/${id}/status`, { status });

// Activities
export const getActivities = (params) => api.get('/activities', { params });
export const getActivity = (id) => api.get(`/activities/${id}`);
export const createActivity = (data) => api.post('/activities', data);
export const updateActivity = (id, data) => api.put(`/activities/${id}`, data);
export const deleteActivity = (id) => api.delete(`/activities/${id}`);
export const getEditHistory = (type, id) => api.get(`/activities/edit-history/${type}/${id}`);

// Action Items (API path and DB table remain 'tasks')
export const getTasks = (params) => api.get('/tasks', { params });
export const getMyTasks = () => api.get('/tasks/my-tasks');
export const getOverdueTasks = () => api.get('/tasks/overdue');
export const getTask = (id) => api.get(`/tasks/${id}`);
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data);
// Partial update — only the supplied fields are written (used by the Kanban board)
export const patchTask = (id, data) => api.patch(`/tasks/${id}`, data);
export const completeTask = (id) => api.post(`/tasks/${id}/complete`);
export const reopenTask = (id) => api.post(`/tasks/${id}/reopen`);
export const deleteTask = (id) => api.delete(`/tasks/${id}`);
export const bulkUpdateTasks = (ids, updates) => api.post('/tasks/bulk-update', { ids, updates });

// Settings
export const getStages = () => api.get('/settings/stages');
export const createStage = (name) => api.post('/settings/stages', { name });
export const updateStage = (id, data) => api.put(`/settings/stages/${id}`, data);
export const reorderStages = (stageIds) => api.post('/settings/stages/reorder', { stageIds });
export const deleteStage = (id) => api.delete(`/settings/stages/${id}`);

export const getLeadSources = () => api.get('/settings/lead-sources');
export const createLeadSource = (name) => api.post('/settings/lead-sources', { name });
export const updateLeadSource = (id, name) => api.put(`/settings/lead-sources/${id}`, { name });
export const deleteLeadSource = (id) => api.delete(`/settings/lead-sources/${id}`);

export const getOwnershipRoleTypes = () => api.get('/settings/ownership-role-types');
export const createOwnershipRoleType = (name) => api.post('/settings/ownership-role-types', { name });
export const updateOwnershipRoleType = (id, name) => api.put(`/settings/ownership-role-types/${id}`, { name });
export const deleteOwnershipRoleType = (id) => api.delete(`/settings/ownership-role-types/${id}`);

export const getOpportunityStagesSettings = () => api.get('/settings/opportunity-stages');
export const createOpportunityStage = (data) => api.post('/settings/opportunity-stages', data);
export const updateOpportunityStage = (id, data) => api.put(`/settings/opportunity-stages/${id}`, data);
export const reorderOpportunityStages = (stageIds) => api.post('/settings/opportunity-stages/reorder', { stageIds });
export const deleteOpportunityStage = (id) => api.delete(`/settings/opportunity-stages/${id}`);

// AI capture: the raw words in, a proposed entry out. Writes nothing (docs/ai-capture.md).
export const draftCapture = (payload) => api.post('/capture/draft', payload);

// Search (the ⌘K palette): one query across every record type the person can see
export const searchAll = (q, limit = 5) => api.get('/search', { params: { q, limit } });

// The assistant: what the person did with a card (the chat itself streams, see assistant/stream.js)
export const reportProposalOutcome = (id, outcome, result) => api.post(`/assistant/proposals/${id}`, { outcome, result });

// The meeting write-up: colleagues off the system, and who attended an activity
export const getInternalPeople = (q) => api.get('/internal-people', { params: q ? { q } : {} });
export const createInternalPerson = (data) => api.post('/internal-people', data);
export const getActivityAttendees = (id) => api.get(`/activities/${id}/attendees`);
export const addActivityAttendees = (id, attendees) => api.post(`/activities/${id}/attendees`, { attendees });

// Saved views: a list page's filters, search and sort under a name
export const getSavedViews = (entity) => api.get('/saved-views', { params: { entity } });
export const createSavedView = (data) => api.post('/saved-views', data);
export const deleteSavedView = (id) => api.delete(`/saved-views/${id}`);
export const reportCaptureOutcomes = (capture_id, items) => api.post('/capture/outcomes', { capture_id, items });

// Today: the person's day by rules (docs/web-foundations.md, Phase 3)
export const getToday = (date) => api.get('/today', { params: { date } });
export const saveTodayPreferences = (muted) => api.put('/today/preferences', { muted });
export const updateTodaySettings = (quiet_days) => api.put('/today/settings', { quiet_days });
export const patchOpportunity = (id, data) => api.patch(`/opportunities/${id}`, data);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');
// AI capture usage and estimated cost, per rep per week (Settings → AI Usage; manage_settings).
export const getAIUsage = (weeks) => api.get('/capture/usage', { params: { weeks } });

// The weekly executive review (managers), and an account's goal and weekly
// summaries (anyone who can see the account).
export const getReviewAccounts = () => api.get('/review/accounts');
export const updateAccountGoal = (id, data) => api.put(`/review/accounts/${id}`, data);
export const getAccountSummaries = (id) => api.get(`/review/accounts/${id}/summaries`);
export const getReviews = (limit = 20) => api.get('/review/reports', { params: { limit } });
export const getReview = (id) => api.get(`/review/reports/${id}`);
export const generateReview = (account_ids, audience) => api.post('/review/reports', { account_ids, audience });
export const updateReviewSection = (reportId, accountId, data) => api.put(`/review/reports/${reportId}/accounts/${accountId}`, data);
export const reviseReviewSection = (reportId, accountId, instruction) => api.post(`/review/reports/${reportId}/accounts/${accountId}/revise`, { instruction });
export const applyReviewChanges = (reportId, accountId, data) => api.post(`/review/reports/${reportId}/accounts/${accountId}/apply`, data);
export const finishReview = (reportId) => api.post(`/review/reports/${reportId}/finish`);
export const deleteReview = (reportId) => api.delete(`/review/reports/${reportId}`);
export const reviewPdfUrl = (reportId) => `${API_BASE_URL}/review/reports/${reportId}/pdf`;


// Permissions
export const getUserPermissions = (userId) => api.get(`/permissions/user/${userId}`);
export const updateUserPermissions = (userId, permissions) => api.put(`/permissions/user/${userId}`, { permissions });
export const getPermissionTemplates = () => api.get('/permissions/templates');
export const applyPermissionTemplate = (userId, templateId) => api.post(`/permissions/user/${userId}/apply-template/${templateId}`);
export const getMyPermissions = () => api.get('/permissions/my');

// Module Visibility
export const getMyModules = () => api.get('/modules/my-modules');
export const getAllModuleDefinitions = () => api.get('/modules');
export const getRoleModuleAccess = (roleId) => api.get(`/modules/role/${roleId}`);
export const updateRoleModuleAccess = (roleId, modules) => api.put(`/modules/role/${roleId}`, { modules });
export const getUserModuleOverrides = (userId) => api.get(`/modules/user/${userId}`);
export const updateUserModuleOverrides = (userId, overrides) => api.put(`/modules/user/${userId}`, { overrides });

// Column Preferences
export const getColumnPreferences = (entityType) => api.get(`/column-preferences/${entityType}`);
export const saveColumnPreferences = (entityType, columns) => api.put(`/column-preferences/${entityType}`, { columns });

// Dashboard Preferences
export const getDashboardPreferences = () => api.get('/dashboard-preferences');
export const saveDashboardPreferences = (cards) => api.put('/dashboard-preferences', { cards });

// Company Settings
export const getCompanySettings = () => api.get('/company-settings');
export const updateCompanySettings = (data) => api.put('/company-settings', data);
export const uploadCompanyLogo = (formData) =>
  api.post('/company-settings/upload-logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// Quote Templates
export const getQuoteTemplates = () => api.get('/quote-templates');
export const getQuoteTemplate = (id) => api.get(`/quote-templates/${id}`);
export const createQuoteTemplate = (data) => api.post('/quote-templates', data);
export const updateQuoteTemplate = (id, data) => api.put(`/quote-templates/${id}`, data);
export const deleteQuoteTemplate = (id) => api.delete(`/quote-templates/${id}`);

// Exports
export const exportAccountsExcel = () => api.get('/export/accounts/excel', { responseType: 'blob' });
export const exportAccountsPdf = () => api.get('/export/accounts/pdf', { responseType: 'blob' });
export const exportContactsExcel = () => api.get('/export/contacts/excel', { responseType: 'blob' });
export const exportContactsPdf = () => api.get('/export/contacts/pdf', { responseType: 'blob' });
export const exportOpportunitiesExcel = () => api.get('/export/opportunities/excel', { responseType: 'blob' });
export const exportOpportunitiesPdf = () => api.get('/export/opportunities/pdf', { responseType: 'blob' });
export const exportQuotesExcel = () => api.get('/export/quotes/excel', { responseType: 'blob' });
export const exportOrdersExcel = () => api.get('/export/orders/excel', { responseType: 'blob' });
export const exportProductsExcel = () => api.get('/export/products/excel', { responseType: 'blob' });

// Reports
export const getReportFilterOptions = () => api.get('/reports/filter-options');
export const getSalesPipelineReport = (params) => api.get('/reports/sales-pipeline', { params });
export const getRevenueReport = (params) => api.get('/reports/revenue', { params });
export const getForecastReport = (params) => api.get('/reports/forecast', { params });
export const getMatrixReport = (params) => api.get('/reports/matrix', { params });
export const getMatrixTable = (params) => api.get('/reports/matrix-table', { params });
export const getReportDrillDown = (params) => api.get('/reports/drill-down', { params });
export const getReportDashboardWidgets = () => api.get('/reports/dashboard-widgets');

// Saved Reports
export const getSavedReports = () => api.get('/reports/saved');
export const createSavedReport = (data) => api.post('/reports/saved', data);
export const updateSavedReport = (id, data) => api.put(`/reports/saved/${id}`, data);
export const deleteSavedReport = (id) => api.delete(`/reports/saved/${id}`);
export const toggleReportFavorite = (id, isFavorite) => api.patch(`/reports/saved/${id}/favorite`, { is_favorite: isFavorite });

// Report Access (Admin)
export const getReportAccess = () => api.get('/reports/access');
export const updateReportAccess = (access) => api.put('/reports/access', { access });

// Report Exports
export const exportReportExcel = (params) => api.get('/reports/export/excel', { params, responseType: 'blob' });
export const exportReportPdf = (data) => api.post('/reports/export/pdf', data, { responseType: 'blob' });

export default api;
